import { useEffect } from 'react'
import approvalSpeedTracker from '../utils/analytics/approval-speed-tracker'

/**
 * Hook for tracking approval speed analytics
 * 
 * This hook provides a clean interface for analytics tracking
 * without polluting business logic components.
 * 
 * @param {number} answerId - The answer being tracked
 * @param {number} questionId - The question ID
 * @param {string} submissionId - The submission ID  
 * @param {boolean} isCurrentlyFocused - Whether this answer is currently focused
 * @returns {Function} calculateApprovalSpeed - Function to calculate approval speed
 */
export const useApprovalSpeedAnalytics = (answerId, questionId, submissionId, isCurrentlyFocused) => {
    // Track focus changes
    useEffect(() => {
        if (!isCurrentlyFocused || !answerId || !questionId || !submissionId) {
            return
        }

        // Check if we need to reset tracking
        if (approvalSpeedTracker.shouldReset(answerId, questionId, submissionId)) {
            approvalSpeedTracker.startTracking(answerId, questionId, submissionId)
        }
    }, [isCurrentlyFocused, answerId, questionId, submissionId])

    // Return function to calculate approval speed
    const calculateApprovalSpeed = () => {
        return approvalSpeedTracker.calculateAndClear(answerId)
    }

    return calculateApprovalSpeed
}

/**
 * Hook for cleanup when component unmounts
 */
export const useApprovalSpeedCleanup = () => {
    useEffect(() => {
        return () => {
            // Clean up all tracking data when component unmounts
            approvalSpeedTracker.clearAll()
        }
    }, [])
} 